import * as THREE from 'three';
import {
  makeCloverTileTexture,
  makeFestiveTileTexture,
  makeFlowerMixTileTexture,
  makeFlowerPinkTileTexture,
  makeFlowerYellowTileTexture,
  makeGardenTileTexture,
  makeGlowTexture,
  makeKidsTileTexture,
  makeLightPoolTexture,
  makeWoodTileTexture,
} from './floorArt';
import {
  makeBrickTileTexture,
  makeCobbleTileTexture,
  makeFlagstoneTileTexture,
  makeGrassTexture,
  makePavingTexture,
} from './helpers';
import type { AttractionPalette, ColorChannel } from './palettes';

function mat(color: number, extras: THREE.MeshStandardMaterialParameters = {}) {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.84,
    metalness: 0.0,
    ...extras,
  });
}

let sharedGlowTexture: THREE.CanvasTexture | null = null;

/** Additive, depth-read-only halo material. Starts invisible; the night look fades it in. */
function glow(color: number, map?: THREE.CanvasTexture) {
  sharedGlowTexture ??= makeGlowTexture();
  return new THREE.MeshBasicMaterial({
    color,
    map: map ?? sharedGlowTexture,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    visible: false,
  });
}

/**
 * Curated toybox/cozy park palette. Plain swatches double as bake sources for
 * vertex-coloured props, so most of them never reach the GPU as separate materials.
 */
export function createMaterials() {
  const grassTexture = makeGrassTexture();
  const pavingTexture = makePavingTexture();
  return {
    baked: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.86, metalness: 0 }),
    bakedGloss: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.5, metalness: 0.05 }),

    grassTop: mat(0xffffff, { map: grassTexture, roughness: 0.92 }),
    grassSide: mat(0x6fb03f, { roughness: 0.92 }),
    grassQuiet: mat(0x9ec47a, { roughness: 0.94 }),
    lawnTile: mat(0xb2e07a, { roughness: 0.9 }),
    soil: mat(0xc98c58, { roughness: 0.95 }),
    bedrock: mat(0x8d6244, { roughness: 0.95 }),
    path: mat(0xffffff, { map: pavingTexture, roughness: 0.88 }),
    pathEdge: mat(0xd8b077, { roughness: 0.9 }),
    curb: mat(0xece0c6, { roughness: 0.85 }),

    // Fable floor family: one texture repetition per 0.48 paving tile; grout swatch matches each joint colour.
    pavingTile: mat(0xffffff, { map: makeFlagstoneTileTexture(), roughness: 0.88 }),
    brickTile: mat(0xffffff, { map: makeBrickTileTexture(), roughness: 0.9 }),
    brickGrout: mat(0xdcc49c, { roughness: 0.92 }),
    cobbleTile: mat(0xffffff, { map: makeCobbleTileTexture(), roughness: 0.86 }),
    cobbleGrout: mat(0x8b97a6, { roughness: 0.9 }),

    // COL-011B floor art refresh: three more path looks + five floral/garden carpets.
    kidsTile: mat(0xffffff, { map: makeKidsTileTexture(), roughness: 0.62 }),
    kidsGrout: mat(0xfbf7ee, { roughness: 0.8 }),
    woodTile: mat(0xffffff, { map: makeWoodTileTexture(), roughness: 0.82 }),
    woodGrout: mat(0x7a4d2a, { roughness: 0.9 }),
    festiveTile: mat(0xffffff, {
      map: makeFestiveTileTexture(),
      roughness: 0.55,
      emissive: 0xffd97a,
      emissiveIntensity: 0,
    }),
    festiveGrout: mat(0xc99a3c, { metalness: 0.35, roughness: 0.4 }),
    flowerPinkTile: mat(0xffffff, { map: makeFlowerPinkTileTexture(), roughness: 0.94 }),
    flowerYellowTile: mat(0xffffff, { map: makeFlowerYellowTileTexture(), roughness: 0.94 }),
    flowerMixTile: mat(0xffffff, { map: makeFlowerMixTileTexture(), roughness: 0.94 }),
    cloverTile: mat(0xffffff, { map: makeCloverTileTexture(), roughness: 0.94 }),
    gardenTile: mat(0xffffff, { map: makeGardenTileTexture(), roughness: 0.9 }),
    floralGrout: mat(0x5f9c3f, { roughness: 0.95 }),
    gardenGrout: mat(0x3f8a3a, { roughness: 0.95 }),

    // COL-011B night pass: additive halos + ground light pools. Opacity is driven by the time of day.
    glowWarm: glow(0xffd58a),
    glowCool: glow(0xbfe4ff),
    lightPool: glow(0xffc36a, makeLightPoolTexture()),

    wood: mat(0xc98a52),
    woodDark: mat(0x8b5a34),
    woodLight: mat(0xe0b078),
    cream: mat(0xfff6e6),
    butter: mat(0xffe3a3),
    stone: mat(0xf1e6d2),
    stoneDark: mat(0xd9c8ac),
    iron: mat(0x3f4a44, { roughness: 0.6, metalness: 0.15 }),
    gold: mat(0xf2c14e, { metalness: 0.45, roughness: 0.32 }),
    red: mat(0xe5514f),
    redDark: mat(0xb83c3c),
    blue: mat(0x4f9ddb),
    teal: mat(0x3fb8a8),
    pink: mat(0xf0879e),
    yellow: mat(0xf7d35c),
    orange: mat(0xf08a3c),
    purple: mat(0x9a6fc9),
    mint: mat(0x7ed6a8),
    sky: mat(0x8ed0f0),
    charcoal: mat(0x4a4a4a),
    water: mat(0x7fd0f0, { roughness: 0.15, metalness: 0.05, emissive: 0x3aa0d8, emissiveIntensity: 0.18 }),

    horseCoat: mat(0xffffff),
    saddle: mat(0xb83c3c),
    skin: mat(0xf5c9a6),
    foliage: mat(0x5cb85a),
    foliageLight: mat(0x7fcf62),
    foliageDark: mat(0x3d9451),
    pine: mat(0x3f8f5e),
    pineLight: mat(0x5aa86c),
    trunk: mat(0xa86a3c),
    hedge: mat(0x4f9e4f),
    lamp: mat(0xfff1c2, { emissive: 0xffd77a, emissiveIntensity: 0.9 }),
    lampPost: mat(0x3d4a3a),
    fence: mat(0xfff8ee),
    rope: mat(0xe0504f),
  };
}

export type ParkMaterials = ReturnType<typeof createMaterials>;

export type ChannelMaterials = Record<ColorChannel, THREE.MeshStandardMaterial>;

export function createChannelMaterials(palette: AttractionPalette): ChannelMaterials {
  return {
    primary: mat(palette.primary, { roughness: 0.62, metalness: 0.02 }),
    secondary: mat(palette.secondary, { roughness: 0.72 }),
    accent: mat(palette.accent, { metalness: 0.4, roughness: 0.34 }),
    structure: mat(palette.structure, { roughness: 0.82 }),
    lights: mat(palette.lights, { emissive: palette.lights, emissiveIntensity: 0.85, roughness: 0.4 }),
  };
}
